import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServiceRouter } from '@pershing/pershing-core';
import { EquityTradingServiceDefinition } from './../../models/equity/equity-trading-service-definition';
import { TransactionType, OrderType, DeskRouteCode, OrderDuration, AccountType, Account, DeskRouteTimeZone, TradingDayInfo } from '../../models/common/trading-common.model';
import { EquityTradingOrder } from '../../models/equity/trading-equity.model';
import { TradingEntitlements } from './../../models/common/trading-entitlements';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    })
};

@Injectable()
export abstract class EquityTradingServiceStrategy {

    private baseUrl: string;
    private platform: string;

    acctNumParam = 'urn:id:headers.x-AccountNumber';
    ordDltParam = 'urn:id:headers.x-OrdDtlKey';

    constructor(protected http: HttpClient, protected serviceRouter: ServiceRouter, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.platform) {
            this.platform = appSettings.platform;
        } else {
            this.platform = 'professional';
        }
        this.baseUrl = this.serviceRouter.getServiceRouteURL(EquityTradingServiceDefinition.getInstance(this.platform), { ignoreOptions: { serviceVersion: true } });
    }

    private getHttpHeadersWithAcctNum(acctNum: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum
        });
    }

    private getHeadersWithAcctNumAndOrdDltKey(acctNum: string,ordDtlKey: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum,
            'x-OrdDtlKey': ordDtlKey
        });
    }

    public abstract support(platform: string): boolean;

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    public abstract getTransactionTypes(account: Account, selectedSession: string): TransactionType[];

    public abstract getOrderTypes(entitlements: TradingEntitlements, isNonDollar: boolean, selectedSession: string,
                                    hasCodeValueExchange: boolean, qty: number, dollarOrFractional: boolean, transactionType: TransactionType): OrderType[];

    public abstract getDurations(selectedSession: string, tradingDayInfo: TradingDayInfo, isNonDollar: boolean, transactionType: TransactionType,
                                    orderType?: string, ordQty?: number, dollarOrFractional?: boolean): OrderDuration[];

    public abstract accountTypes(account: Account, isNonDollar: boolean, transactionType: TransactionType, isGTCOrder: boolean, symAccountType: AccountType): AccountType[];

    public abstract getDeskRouteTimeZone(): DeskRouteTimeZone[];

    public abstract getDeskCode(): DeskRouteCode[];

    public abstract getCodeValuesExchangeList(): Observable<any>;

    public abstract getCodeValuesFloorInstructions(): Observable<any>;

    public reviewEquityOrder(equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/review', equityTradingOrder, httpOptions);
    }

    public submitEquityOrder(equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/submit', equityTradingOrder, httpOptions);
    }

    public correctEquityOrder(equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/correct-order', equityTradingOrder, httpOptions);
    }

    public cancelEquityOrder(acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        const encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        const url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/Equity/cancel/${this.ordDltParam}`;
        return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public getShortSaleRebate(account: string, symId: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + this.acctNumParam + '/' + symId + '/indicative-rate';
        return this.http.get(url, { headers: this.getHttpHeadersWithAcctNum(account) });
    }
}
